import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "dvajs";
import { changeEgovUrl } from "@/utils/tools";
import { message as toast } from "antd";
import DeeplinkEvent from "@/components/DeeplinkHandler/DeeplinkEvent";
import { FocusSDK } from "@/utils";
import imgDefault from "@/components/DefaultPage/imgs/img-load-failed.png";
import { addXtoken } from "@/utils/addToken";
import IconFont from "@/components/icon";
import lodashGet from "lodash/get";
import styles from "./index.module.less";
import TextCard from "./TextCard";
import { MultiCardTemplateType } from "@/utils/chat/message";

interface IProps {
  message: any;
  selectedSession: Function;
}

// 默认展示
const DEFAULT_SHOW_LEN = 1;

// 多图文卡片
function MultiImageTextCard({ message, selectedSession }: IProps) {
  const isPrivateNet = useRef<boolean>(FocusSDK.isPrivateNet());
  const [showMore, setShowMore] = useState<boolean>(false);
  // 数据
  const dataInfo = useMemo(() => {
    const data = lodashGet(message, "data.0", {});
    let d;
    if (data?.orgNumberCard) {
      d = data?.orgNumberCard;
    } else {
      d = data;
    }
    return d || {};
  }, [message]);

  // 文本卡片
  const isTextCard = useMemo(() => {
    return dataInfo?.type === MultiCardTemplateType.Text;
  }, []);

  // 单图文卡片
  const isImageText = useMemo(() => {
    return dataInfo?.type === MultiCardTemplateType.ImageText;
  }, []);

  // 多图文
  const isMultiImageText = useMemo(() => {
    return dataInfo?.type === MultiCardTemplateType.MultiImageText;
  }, []);

  // 点击跳转
  const handleClickLink = useCallback((url: string) => {
    if (!url) {
      return;
    }
    const parseUrl = DeeplinkEvent.parseDeeplink(url);
    if (!parseUrl) {
      toast.warn("当前跳转链接不符合规范");
      return;
    }
    if (parseUrl.pathname && parseUrl.pathname.includes("browser")) {
      const mparam = parseUrl.mparam || {};
      let realUrl = isPrivateNet.current ? mparam.egovUrl : mparam.url;
      if (!realUrl) {
        toast.warn("该应用不支持在当前网络环境下打开");
        return;
      }
      window.open(realUrl);
    } else {
      DeeplinkEvent.open(url);
    }
  }, []);

  const getLinkClass = useCallback((link: string) => {
    if (link) {
      return "cursor-pointer";
    } else {
      return "";
    }
  }, []);

  const imgOnError = (e: any) => {
    try {
      if (e.target) {
        e.target.src = imgDefault;
        e.target.className = "error";
      }
    } catch (e) {}
  };

  const getRealImageUrl = useCallback((url: string, egovUrl: string) => {
    return addXtoken(url);
  }, []);

  const hasMainImage = useMemo(() => {
    return dataInfo?.imageUrl;
  }, [dataInfo]);

  // 主图
  const mainImage = useMemo(() => {
    return hasMainImage ? (
      <div className={`${styles["main-wrap"]}`} onClick={() => handleClickLink(dataInfo?.url)}>
        {dataInfo?.imageUrl && (
          <div className={styles["img-wrap"]}>
            <img src={dataInfo?.imageUrl} onError={imgOnError} />
            {isMultiImageText && dataInfo?.title && (
              <span className={`${styles["main-title"]} three-lines`}>{dataInfo?.title}</span>
            )}
          </div>
        )}
      </div>
    ) : null;
  }, [dataInfo, getLinkClass, handleClickLink, hasMainImage]);

  // 展开更多
  const hasMore = useMemo(() => {
    return (dataInfo?.contentList?.length || 0) > DEFAULT_SHOW_LEN;
  }, [dataInfo]);

  const resetNum = useMemo(() => {
    return dataInfo?.contentList?.length - DEFAULT_SHOW_LEN;
  }, [dataInfo]);

  // 显示图文列表
  const showImageList = useMemo(() => {
    if (!hasMore) {
      return dataInfo?.contentList || [];
    } else if (!showMore) {
      return (dataInfo?.contentList || []).slice(0, DEFAULT_SHOW_LEN);
    } else {
      return dataInfo?.contentList || [];
    }
  }, [hasMore, dataInfo, showMore]);

  const handleShowMore = useCallback(
    (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      setShowMore(!showMore);
    },
    [showMore]
  );
  // 图文列表
  const imageList = useMemo(() => {
    if (!dataInfo?.contentList || !dataInfo?.contentList?.length) {
      return null;
    }
    return (
      <div className={`${styles["info-list-wrap"]} ${showMore ? styles["more"] : ""}`}>
        <div className={styles["content"]}>
          {showImageList.map((item: any, idx: number) => {
            const nolink = !item?.linkName || !item?.pcUrl;
            return (
              <div
                className={`${styles["info-item"]} ${getLinkClass(item?.url)} ${
                  item?.imageUrl ? "" : styles["no-img"]
                } ${styles[dataInfo?.imagePosition]}`}
                key={`${message.id}_img_${idx}`}
                onClick={() => handleClickLink(item?.url)}
              >
                {item?.imageUrl && (
                  <div className={styles["img-wrap"]}>
                    <img src={item?.imageUrl} onError={imgOnError} />
                  </div>
                )}
                <div className={styles["info-content"]}>
                  <span
                    className={`${styles["text"]} ${
                      !nolink ? styles["two-lines"] : styles["all-line"]
                    }`}
                    dangerouslySetInnerHTML={{ __html: item?.content }}
                  />
                  {!nolink && (
                    <span className={`${styles["link"]} ellipsis`}>{item?.linkName}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {hasMore ? (
          <div className={styles["footer"]} onClick={handleShowMore}>
            <span className={styles["text"]}>{`余下${resetNum}篇`}</span>
            <IconFont
              className={styles["icon"]}
              type={`${!showMore ? "iconicon_down" : "iconicon_up"}`}
            />
          </div>
        ) : null}
      </div>
    );
  }, [
    dataInfo?.contentList,
    showMore,
    showImageList,
    hasMore,
    resetNum,
    getLinkClass,
    message.id,
    handleClickLink
  ]);

  // 单图文区域
  const imageTextContent = useMemo(() => {
    return (
      <div
        className={styles["image-text-content-wrap"]}
        onClick={() => handleClickLink(dataInfo?.url)}
      >
        <span className={`${styles["title"]} ellipsis`}>{dataInfo?.title}</span>
        <div className={`${styles["content"]} two-lines`}>{dataInfo?.content}</div>
      </div>
    );
  }, []);

  // 内容区
  const contentWrap = useMemo(() => {
    return isMultiImageText ? imageList : imageTextContent;
  }, [isMultiImageText, imageList, imageTextContent]);

  return isTextCard ? (
    <TextCard message={message} />
  ) : (
    <div className={`${styles["multi-imgcard-content-wrap"]} ${getLinkClass(dataInfo?.url)}`}>
      {mainImage}
      {contentWrap}
    </div>
  );
}

function mapStateToProps({ chat }: any) {
  return {
    selectedSession: chat.selectedSession
  };
}

function mapDispatchToProps(dispatch: any) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(MultiImageTextCard);
